import styled from "@emotion/styled/macro";
import {
  tokens,
  useSingletonMobileMediaQuery,
} from "@sunrun/experience-ui-components";
import { ReactNode } from "react";
import {
  ReviewFlexMonthlySummary,
  ReviewOfferProduct,
} from "../../../../amplify/backend/function/offerexpstoreFrontApi/ts/public/offerTypes";
import { formatCurrency, formatPercent } from "../../../utils/formatHelpers";
import {
  getBatteryYearOneMonthlyPayment,
  getYearOneMonthlyEnergyPaymentDisplay,
} from "./offerReviewPaymentProductAmounts";
import {
  COMPARABLE_RETAIL_BATTERY_FOOTNOTE,
  ESTIMATED_UTILITY_BILL_PAYMENTS_TOOLTIP,
  FLEX_MINIMUM_ENERGY_PAYMENT_TOOLTIP,
} from "./offerReviewPaymentCopy";
import {
  getFlexAdditionalAnnualKwhDelta,
  getFlexYear1AdditionalEnergyTooltip,
  getFlexYear1MarginalRatePerKwh,
} from "./offerReviewPaymentUtils";
import {
  PaymentCard,
  PaymentColumn,
  PaymentColumns,
  PaymentFinePrint,
  PaymentGroup,
  PaymentGroupTitle,
  PaymentLineItem,
  PaymentRows,
  PaymentValidUntil,
  PaymentValue,
} from "./OfferReviewPaymentPrimitives";
import { OfferReviewInfoTooltip } from "components/atoms/OfferReviewInfoTooltip";

type FlexMonthlyPaymentDetailsProps = {
  summary: ReviewFlexMonthlySummary;
  consumption: number;
  production: number;
  products?: ReviewOfferProduct[];
  showValidUntil: boolean;
  children?: ReactNode;
};

const FlexMonthlyPaymentDetails = ({
  summary,
  consumption,
  production,
  products,
  showValidUntil,
  children,
}: FlexMonthlyPaymentDetailsProps) => {
  const isMobile = useSingletonMobileMediaQuery();
  const batteryPayment = getBatteryYearOneMonthlyPayment(products);
  const flexRatePerKwh = getFlexYear1MarginalRatePerKwh(summary);
  const additionalKwhDelta = getFlexAdditionalAnnualKwhDelta(
    consumption,
    production
  );
  const flexRateTooltip = getFlexYear1AdditionalEnergyTooltip(
    summary,
    consumption,
    production
  );
  const energyPayment = getYearOneMonthlyEnergyPaymentDisplay(
    summary.minimumMonthlyPayment,
    products
  );

  const estimatedUtilityBillPayments = (
    <PaymentGroup>
      <GroupTitleRow>
        <PaymentGroupTitle
          size={14}
          fontWeight="medium"
          color={tokens.BRAND_HERO_BLUE}
        >
          Estimated Utility Bill Payments
        </PaymentGroupTitle>
        <OfferReviewInfoTooltip
          content={ESTIMATED_UTILITY_BILL_PAYMENTS_TOOLTIP}
          ariaLabel="About estimated utility bill payments"
          maxWidth={480}
          iconSize={14}
          testId="estimated-utility-bill-tooltip"
        />
      </GroupTitleRow>
      <PaymentRows>
        <PaymentLineItem
          label="Avg. est. pre-Sunrun utility bill"
          value={formatCurrency(summary.preSolarUtility, 2)}
          unit="/mo"
          valueSize={14}
          valueLineHeight={1.4}
        />
        <PaymentLineItem
          label="Avg. est. post-Sunrun utility bill"
          value={formatCurrency(summary.postSolarUtility, 2)}
          unit="/mo"
          valueSize={14}
          valueLineHeight={1.4}
        />
      </PaymentRows>
    </PaymentGroup>
  );

  const lockingInPayments = (
    <PaymentGroup>
      <PaymentGroupTitle
        size={14}
        fontWeight="medium"
        color={tokens.BRAND_HERO_BLUE}
      >
        Locking in payments with your Agreement Terms:
      </PaymentGroupTitle>
      <PaymentRows>
        <PaymentLineItem
          label="Annual escalator on monthly payment"
          value={formatPercent(summary.annualEscalator)}
          valueSize={14}
          valueLineHeight={1.4}
        />
        <PaymentLineItem
          label="Agreement term"
          value={String(summary.termYears)}
          unit="years"
          valueSize={14}
          valueLineHeight={1.4}
        />
        <DueAtSigning $size={14} $weight={600} $lineHeight={1.4}>
          {formatCurrency(summary.dueAtSigning, 0)} due at signing
        </DueAtSigning>
      </PaymentRows>
    </PaymentGroup>
  );

  const promotionalBatteryPayment = batteryPayment != null && (
    <PaymentGroup $tight>
      <PaymentRows>
        <PaymentLineItem
          label="Promotional battery payment"
          labelSize={20}
          value={formatCurrency(batteryPayment, 2)}
          unit="/mo"
          valueSize={20}
        />
      </PaymentRows>
      <PaymentFinePrint data-testid="offer-review-payment-group-footnote">
        {COMPARABLE_RETAIL_BATTERY_FOOTNOTE}
      </PaymentFinePrint>
    </PaymentGroup>
  );

  const energyPaymentGroup = energyPayment && (
    <PaymentGroup>
      <PaymentRows>
        <EnergyPaymentLineItem
          label="Year 1 Minimum Monthly Energy Payment"
          labelSize={20}
          labelAccessory={
            <OfferReviewInfoTooltip
              content={FLEX_MINIMUM_ENERGY_PAYMENT_TOOLTIP}
              ariaLabel="About the minimum monthly energy payment"
              testId="flex-minimum-energy-payment-tooltip"
            />
          }
          value={energyPayment.value}
          unit={energyPayment.unit}
          valueSize={20}
          maxLabelWidth={400}
        />
        <EnergyPaymentLineItem
          label="Year 1 Sunrun flex rate"
          labelSize={20}
          labelAccessory={
            <OfferReviewInfoTooltip
              content={flexRateTooltip}
              ariaLabel="About the year 1 Sunrun flex rate"
              testId="flex-additional-energy-payment-tooltip"
            />
          }
          value={formatCurrency(flexRatePerKwh, 3)}
          unit="/kWh"
          valueSize={20}
        />
        <PaymentFinePrint data-testid="offer-review-additional-energy-payment-footnote">
          for up to an additional est. {additionalKwhDelta} annual kWh consumed
        </PaymentFinePrint>
      </PaymentRows>
    </PaymentGroup>
  );

  return (
    <PaymentCard $opacity={0.4} data-testid="offer-review-simplified-payment">
      <PaymentColumn>
        <PaymentRows>
          <PaymentColumns $isMobile={isMobile} $columnGap={32}>
            <PaymentColumn>{energyPaymentGroup}</PaymentColumn>
            <PaymentColumn>{promotionalBatteryPayment}</PaymentColumn>
          </PaymentColumns>
        </PaymentRows>
        <PaymentRows>
          <PaymentColumns $isMobile={isMobile} $columnGap={32}>
            <PaymentColumn>{estimatedUtilityBillPayments}</PaymentColumn>
            <PaymentColumn>{lockingInPayments}</PaymentColumn>
          </PaymentColumns>
        </PaymentRows>
      </PaymentColumn>
      {children}
      {showValidUntil && (
        <ValidUntilRow>
          <RightAlignedValidUntil expires={summary.expires} />
        </ValidUntilRow>
      )}
    </PaymentCard>
  );
};

const GroupTitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const EnergyPaymentLineItem = styled(PaymentLineItem)``;

const DueAtSigning = styled(PaymentValue)`
  align-self: flex-end;
`;

const ValidUntilRow = styled.div`
  display: flex;
  justify-content: flex-end;
  width: 100%;
  padding-top: 4px;
`;

const RightAlignedValidUntil = styled(PaymentValidUntil)`
  && {
    line-height: 1 !important;
    text-align: right;
  }
`;

export { FlexMonthlyPaymentDetails };
